// Sample events calendar build, explained and detailed over at
// https://justacoding.blog/react-calendar-component-example-with-events/

import PanelComponent from 'components/panel'
import ToastComponent from 'components/toast'
import { memo, useEffect, useRef, useState } from 'react'
import {
  CalendarGridProps,
  CalendarInputProps,
  CalendarProps,
  CalendarState,
  CalendarType,
  getCarpoolDriverSelectedDates,
  getInitialSelectedDatesState,
} from 'types/CalendarTypes'
import { MessageProps } from 'types/CommonTypes'
import { DateCarpoolProps, DateTimeProps } from 'types/CommunityTypes'
import { getCalendarDateRange, MAX_ALLOWED_CARPOOL_UPDATES_PER_REQUEST, pluralize } from 'utils/constants'
import CalendarGridComponent from './CalendarGridComponent'
import CalendarNavigationComponent from './CalendarNavigationComponent'
import CalendarRequestUpdateComponent from '../calendar-request-update'
import CalendarUpdateComponent from '../calendar-update'
import './index.css'

// List of day abbreviations for calendar headers
const DAYS: string[] = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

// Arrays for weekend and weekday abbreviations
const WEEKENDS: string[] = DAYS.filter((d) => d === 'Su' || d === 'Sa')
const WEEKDAYS: string[] = DAYS.filter((d) => !WEEKENDS.includes(d))

// Calendar grid constants
const GRID_WEEKS_COUNT: number = 6 // Always show 6 weeks for consistent grid
const DAYS_PER_WEEK: number = 7
const TOTAL_GRID_CELLS: number = GRID_WEEKS_COUNT * DAYS_PER_WEEK

/**
 * Clamps a date between min and max bounds.
 * Returns the bounded date.
 */
const getBoundedDate = (date: Date, min: Date, max: Date): Date => {
  const d = new Date(date)
  if (d < min) return new Date(min)
  if (d > max) return new Date(max)
  return d
}

/**
 * Generates the calendar grid for the given month, filling in dates and matching events.
 * @param showDate The current month/year to display
 * @param daysOfWeek Array of day names to include in the grid (e.g., ['Mo', 'Tu', ...])
 * @param events Array of event objects with an eventDate field (YYYY-MM-DD)
 * @returns An array of CalendarGridProps for each grid cell.
 */
const generateCalendarGrid = (
  showDate: Date,
  daysOfWeek: string[],
  events: CalendarInputProps[],
): CalendarGridProps[] => {
  const monthsEvents: CalendarGridProps[] = []

  // Start from the closest previous Monday (or first day in daysOfWeek)
  const gridStartDate = new Date(showDate.getFullYear(), showDate.getMonth(), 1, 0, 0, 0, 0)
  // Adjust to previous Monday to align the grid
  gridStartDate.setDate(gridStartDate.getDate() - (gridStartDate.getDay() - 1))

  for (let i = 0; i < TOTAL_GRID_CELLS; i++) {
    // Clone the current grid date for this cell
    const d = new Date(gridStartDate)
    // Move gridStartDate to the next day for the next iteration
    gridStartDate.setDate(gridStartDate.getDate() + 1)

    // Only include days that are in the configured daysOfWeek
    if (!daysOfWeek.includes(DAYS[d.getDay()])) continue

    // Format date as YYYY-MM-DD for matching
    const eventDate: string = d.toISOString().split('T')[0]
    // Find a matching event or create an empty slot
    const event = events.find((e) => e.eventDate === eventDate)
    monthsEvents.push(event || { eventDate })
  }
  return monthsEvents
}

// CalendarComponent
// Main entry point for the calendar UI, handles navigation, date selection, and event-specific settings dialogs.
// Allows users to select one or more dates and, depending on the event type, configure carpool times or pickup drivers.

export const CalendarComponent = memo(
  ({
    type,
    events,
    carpoolRequestOptions,
    carpoolOptions,
    onCarpoolRequest,
    onCarpool,
    showDate: initialShowDate = new Date(),
    showWeekends = false,
    isReadOnly = false,
  }: CalendarProps) => {
    // 1. State Hooks
    // State for currently selected dates (as YYYY-MM-DD strings)
    const [selectedDatesByState, setSelectedDatesByState] =
      useState<Record<CalendarState, string[]>>(getInitialSelectedDatesState())
    const settingsRef = useRef<HTMLDivElement>(null)
    const [msg, setMsg] = useState<MessageProps>()

    // 2. Derived Constants
    // Get the allowed calendar date range (e.g., July 4 last year to July 4 next year)
    const { startDate, endDate } = getCalendarDateRange()
    // Clamp the initial show date within the allowed range
    const [showDate, setShowDate] = useState<Date>(() => getBoundedDate(initialShowDate, startDate, endDate))
    // Use all days or just weekdays depending on showWeekends
    const daysOfWeek: string[] = showWeekends ? [...DAYS] : [...WEEKDAYS]
    // Indicates if we have scrolled to settings
    const isComponentLoaded = carpoolOptions || carpoolRequestOptions ? true : false
    const hasScrolledToSettings = useRef(false)

    // 3. Effect Hooks
    // Reset selected dates when the calendar type or events change
    useEffect(() => {
      setSelectedDatesByState(getInitialSelectedDatesState())
    }, [type, events])

    useEffect(() => {
      // Scroll to settings when a cell is selected for the first time
      const selectedDates = Object.values(selectedDatesByState).flat()
      if (isComponentLoaded && selectedDates.length > 0 && !hasScrolledToSettings.current) {
        // Wait for DOM to render the settings panel before scrolling
        setTimeout(() => {
          if (settingsRef.current) {
            settingsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
            // Optionally also scroll window if needed:
            // window.scrollTo({ top: settingsRef.current.offsetTop, behavior: 'smooth' });
            hasScrolledToSettings.current = true
          }
        }, 100) // 100ms is usually safe, you can try 0 or 50ms if you want it faster
      } else if (!isComponentLoaded || selectedDates.length === 0) {
        hasScrolledToSettings.current = false
      }
    }, [isComponentLoaded, selectedDatesByState])

    // 4. Derived State, Validations and Early Returns
    // Generate the calendar grid events for the current month
    const gridEvents: CalendarGridProps[] = generateCalendarGrid(showDate, daysOfWeek, events)

    // Get the user selected dates and determine if the review or bulk update panel should be shown and panel title
    const selectedDates = Object.values(selectedDatesByState).flat()
    const selectedDatesCount: number = selectedDates.length
    const selectedEventDate: string =
      selectedDatesCount > 0
        ? new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(
            new Date(`${selectedDates[selectedDatesCount - 1]}T00:00:00`),
          )
        : ''
    const selectedEvents = events.filter((e) => selectedDates.includes(e.eventDate))
    const panelTitle: string =
      selectedDatesCount === 1 && type === CalendarType.CARPOOL
        ? `Review & Update: ${selectedEventDate}`
        : selectedDatesCount === MAX_ALLOWED_CARPOOL_UPDATES_PER_REQUEST
          ? `You've reached the update limit of ${pluralize(selectedDatesCount, 'day')}`
          : `Review & Update: ${pluralize(selectedDatesCount, 'Day')}`

    // 5. Event Handlers
    // Toggle a date in the selectedDates array
    const handleDateClick = (date: string, state: CalendarState) => {
      setSelectedDatesByState((prev) => {
        const currentSelectedDates = Object.values(prev).flat()
        const isDateSelected = prev[state].includes(date)

        // If date is already selected, allow deselecting it regardless of the limit
        if (isDateSelected) {
          return {
            ...prev,
            [state]: prev[state].filter((d) => d !== date),
          }
        }

        // Prevent selecting more than MAX_ALLOWED_CARPOOL_UPDATES_PER_REQUEST dates
        if (currentSelectedDates.length >= MAX_ALLOWED_CARPOOL_UPDATES_PER_REQUEST) {
          setMsg({
            style: 'warning',
            text: `You can only select up to ${MAX_ALLOWED_CARPOOL_UPDATES_PER_REQUEST} dates at a time`,
          })
          return prev
        }

        // Add the new date
        return {
          ...prev,
          [state]: [...prev[state], date],
        }
      })
    }
    const handleCarpoolRequest = async (body: DateTimeProps) => {
      if (!onCarpoolRequest) return
      await onCarpoolRequest(body)
      setSelectedDatesByState(getInitialSelectedDatesState())
    }
    const handleCarpool = async (body: DateCarpoolProps) => {
      if (!onCarpool) return
      await onCarpool(body)
      setSelectedDatesByState(getInitialSelectedDatesState())
    }
    const handleCancel = () => setSelectedDatesByState(getInitialSelectedDatesState())

    // 6. Render
    return (
      <div className='calendar' data-testid='dates-testid'>
        {msg && <ToastComponent {...msg} onClose={() => setMsg(undefined)} />}

        {/* Top navigation bar for month/year and prev/next */}
        <CalendarNavigationComponent
          showDate={showDate}
          setShowDate={setShowDate}
          startDate={startDate}
          endDate={endDate}
        />

        {/* Main calendar grid is always rendered */}
        <CalendarGridComponent
          type={type}
          gridEvents={gridEvents}
          daysOfWeek={daysOfWeek}
          selectedDates={selectedDates}
          onClick={!isReadOnly ? handleDateClick : undefined}
        />

        {/* Render preview and bulk update if settings are available */}
        {!isReadOnly && selectedDatesCount > 0 && (
          <div ref={settingsRef} className='w-full'>
            <PanelComponent title={panelTitle} onCancel={handleCancel}>
              {/* Bulk Update */}
              {type === CalendarType.CARPOOL_REQUEST && carpoolRequestOptions && (
                <CalendarRequestUpdateComponent
                  selectedDates={selectedDates}
                  carpoolRequestOptions={carpoolRequestOptions}
                  onSave={handleCarpoolRequest}
                />
              )}
              {type === CalendarType.CARPOOL && carpoolOptions && (
                <CalendarUpdateComponent
                  selectedDatesByState={getCarpoolDriverSelectedDates(selectedDatesByState)}
                  selectedEvents={selectedEvents}
                  carpoolOptions={carpoolOptions}
                  onSave={handleCarpool}
                />
              )}
            </PanelComponent>
          </div>
        )}
      </div>
    )
  },
)

CalendarComponent.displayName = 'CalendarComponent'
